import { NUMBER_FILTER_PARAMS, TEXT_FILTER_PARAMS } from '@/pages/Studies/utils/sharedColumnDefs';
import type { ProteoformResultsTableConfig, SampleProteoformPublic } from '@/types';
import type { ColDef } from 'ag-grid-enterprise';

type TFunction = (key: string) => string;

const stub = (colId: string): ColDef<SampleProteoformPublic> => ({
  colId,
  headerName: colId,
  resizable: true,
  sortable: false,
  filter: false,
});

/**
 * Builds the dynamic isoform/PTM columns from the assay's proteoform results table config
 * (POR-322 `/assays/{assay_id}/proteoform_results_table_config`). These remain display-only
 * stub columns (no per-column data yet, same as before) until the run-scoped proteoform
 * endpoint exposes structured per-column values — this only replaces the previously-hardcoded
 * column *set and labels* with the real assay-driven ones.
 */
function buildDynamicColumns(tableConfig: ProteoformResultsTableConfig | undefined): ColDef<SampleProteoformPublic>[] {
  if (!tableConfig) return [];
  const isoform = tableConfig.isoform_columns.toSorted((a, b) => a.index - b.index);
  const ptm = tableConfig.ptm_columns.toSorted((a, b) => a.index - b.index);
  // `col.key` is the stable identifier (matches the key used in proteoform_name matching
  // elsewhere); `col.label` is just display text and could change independently — use it only
  // for headerName, not colId, to avoid unstable column IDs / AG Grid state loss.
  return [...isoform, ...ptm].map(col => ({ ...stub(col.key), headerName: col.label }));
}

export function buildRunSampleDetailsColDefs(
  t: TFunction,
  tableConfig?: ProteoformResultsTableConfig,
): ColDef<SampleProteoformPublic>[] {
  return [
    stub('Flowcell'), // TODO: no field in SampleProteoformPublic — add when run-scoped endpoint ships
    stub('Lane'), // TODO: no field in SampleProteoformPublic
    {
      field: 'well_position',
      headerName: t('col_well'),
      filter: 'agTextColumnFilter',
      filterParams: TEXT_FILTER_PARAMS,
      resizable: true,
      sortable: true,
    },
    {
      field: 'demux_index',
      headerName: t('col_demux_id'),
      filter: 'agNumberColumnFilter',
      filterParams: NUMBER_FILTER_PARAMS,
      resizable: true,
      sortable: true,
    },
    {
      field: 'sample_name',
      headerName: t('col_sample'),
      filter: 'agTextColumnFilter',
      filterParams: TEXT_FILTER_PARAMS,
      resizable: true,
      sortable: true,
    },
    stub('Sample Groups'), // TODO: sample_group not in SampleProteoformPublic
    {
      field: 'proteoform_name',
      headerName: t('col_proteoform_group'),
      filter: 'agTextColumnFilter',
      filterParams: TEXT_FILTER_PARAMS,
      resizable: true,
      sortable: true,
    },
    {
      field: 'proteoform_abundance',
      headerName: t('col_abundance'),
      filter: 'agNumberColumnFilter',
      filterParams: NUMBER_FILTER_PARAMS,
      resizable: true,
      sortable: true,
      valueFormatter: ({ value }: { value: number | null | undefined }) => value?.toLocaleString() ?? '',
    },
    // Isoform + PTM columns, sourced from the assay's proteoform results table config
    // (POR-322) instead of a hardcoded Tau-only column set. While the config hasn't loaded
    // yet, these are simply omitted (no columns rendered) rather than showing stale stubs.
    ...buildDynamicColumns(tableConfig),
  ];
}
